import { useLocalSearchParams } from 'expo-router'
import type { LucideIcon } from 'lucide-react-native'
import {
    Bell,
    Building2,
    Calendar,
    Clock,
    Globe,
    ImageIcon,
    Lock,
    Route,
    Settings2,
    Timer,
    Users,
} from 'lucide-react-native'
import { useEffect, useMemo, useState } from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'
import {
    Chip,
    Dialog,
    Divider,
    type MD3Theme,
    Portal,
    Switch,
    Text,
    useTheme,
} from 'react-native-paper'
import DateTimePicker from '@/components/DateTimePicker'
import { LogoUpload } from '@/components/LogoUpload'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { WorkingHoursForm } from '@/components/WorkingHoursForm'
import { PERIOD_TYPES, WEEK_DAY_NAMES } from '@/constants/periods'
import { COMMON_TIMEZONES } from '@/constants/timezones'
import { useMembers } from '@/hooks/people'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useLockPeriod } from '@/hooks/useLockPeriod'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { useOrgSettings, useUpdateOrgSettings } from '@/hooks/useOrgSettings'
import { BREAKPOINTS } from '@/lib/config'
import { notify } from '@/lib/notice'
import { getBiweeklyHelperText, getPeriodInfo } from '@/lib/periods'
import {
    getDefaultFormValues,
    type OrgSettingsFormData,
    orgSettingsSchema,
} from '@/stores/org-settings-store'
import type { WeekStartDay } from '@/types/periods'
import { Button } from '@/ui/Button'
import { Card } from '@/ui/Card'
import { MenuButton, NumberInput, useForm, zodResolver } from '@/ui/form'
import { Page } from '@/ui/Page'
import { JobPicker } from '@/ui/time-entry/JobPicker'

export default function OrganizationSettingsPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const { org } = useOrgBySlug(params.orgSlug)
    const theme = useTheme()
    const { width } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg

    const { settings } = useOrgSettings(params.orgSlug)
    const updateSettings = useUpdateOrgSettings(settings)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { isDirty },
    } = useForm<OrgSettingsFormData>({
        resolver: zodResolver(orgSettingsSchema),
        defaultValues: getDefaultFormValues(null),
        mode: 'onChange',
    })

    // Reset the form when settings first arrive (id transitions from undefined
    // to a value). Avoid resetting on every realtime tick — pbtsdb's live
    // query emits a fresh `settings` reference on every realtime event, even
    // when the data is unchanged. Resetting on those would wipe the user's
    // in-progress edits before they can click Save.
    const settingsId = settings?.id
    // biome-ignore lint/correctness/useExhaustiveDependencies: only react to id changes; settings/reset are intentionally not dependencies.
    useEffect(() => {
        if (settingsId) {
            reset(getDefaultFormValues(settings))
        }
    }, [settingsId])

    const onSavePress = handleSubmit(
        data =>
            updateSettings.mutate({
                data,
                orgSlug: params.orgSlug,
                // Clear isDirty before the hook navigates back; otherwise
                // UnsavedChangesDialog intercepts the navigation and asks to
                // discard changes that have already been saved.
                onSaved: () => reset(data),
            }),
        errors => {
            // Surface validation failures so a click on Save never silently
            // no-ops — pick the first message and toast it.
            const first = Object.values(errors).find(e => e?.message)?.message
            notify.error(first ?? 'Some settings are invalid. Please review the form.')
        }
    )

    useHeaderOptions({
        buttons: {
            type: 'save',
            onPress: onSavePress,
            loading: updateSettings.isPending,
            loadingTitle: 'Saving...',
        },
    })

    const formValues = watch()

    // Auto-reset prefer_non_hq_job when its gating conditions go away. Without
    // this, a user can toggle it on, then clear HQ or switch travel to "none",
    // and the now-disabled switch's hidden `true` value would fail Zod's
    // refine on save with no clear way for the user to fix it.
    useEffect(() => {
        if (!formValues.prefer_non_hq_job) return
        if (!formValues.headquarters_job || formValues.track_travel_time === 'none') {
            setValue('prefer_non_hq_job', false, { shouldDirty: true })
        }
    }, [
        formValues.prefer_non_hq_job,
        formValues.headquarters_job,
        formValues.track_travel_time,
        setValue,
    ])

    const periodPreview = getPeriodInfo(new Date(), {
        periodType: formValues.period_type,
        weekStartDay: formValues.week_start_day as WeekStartDay,
    })

    const showWeekStartDay =
        formValues.period_type === 'weekly' ||
        formValues.period_type === 'biweekly_even' ||
        formValues.period_type === 'biweekly_odd'

    const { members: membersData } = useMembers(params.orgSlug)
    const supervisorOptions = useMemo(
        () =>
            membersData.members
                .filter(m => m.role === 'admin' || m.role === 'clerical')
                .map(m => ({
                    value: m.user.id,
                    label: m.user.name || m.user.email,
                })),
        [membersData.members]
    )

    const selectedSupervisors = formValues.default_supervisors ?? []

    return (
        <Page>
            <PeriodLockingCard isDesktop={isDesktop} theme={theme} settings={settings} />

            <Card>
                <Card.Content style={styles.cardContent}>
                    <Text variant="titleLarge" style={styles.sectionTitle}>
                        Organization Settings
                    </Text>

                    <View style={styles.section}>
                        {org && (
                            <>
                                <SettingRow isDesktop={isDesktop}>
                                    <SettingInfo
                                        icon={ImageIcon}
                                        title="Organization Logo"
                                        description="Upload a logo to display in the sidebar and dashboard. A horizontal logo works best."
                                        theme={theme}
                                    />
                                    <SettingControl isDesktop={isDesktop}>
                                        <LogoUpload
                                            orgId={org.id}
                                            logo={org.logo}
                                            disabled={updateSettings.isPending}
                                        />
                                    </SettingControl>
                                </SettingRow>
                                <Divider />
                            </>
                        )}

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Calendar}
                                title="Pay Period Type"
                                description="Select how time entries are grouped for payroll. This affects the timesheet view for all users."
                                theme={theme}
                            >
                                {periodPreview && (
                                    <Text
                                        variant="bodySmall"
                                        style={[
                                            styles.periodPreview,
                                            { color: theme.colors.primary },
                                        ]}
                                    >
                                        Current period: {periodPreview.label}
                                    </Text>
                                )}
                            </SettingInfo>
                            <SettingControl isDesktop={isDesktop}>
                                <MenuButton
                                    name="period_type"
                                    control={control}
                                    buttonStyle={styles.menuButton}
                                    wrapperProps={{ testID: 'period-type-menu' }}
                                    options={Object.entries(PERIOD_TYPES).map(([key, label]) => ({
                                        value: key,
                                        label,
                                        helperText:
                                            key === 'biweekly_even' || key === 'biweekly_odd'
                                                ? getBiweeklyHelperText(
                                                      key as 'biweekly_even' | 'biweekly_odd'
                                                  )
                                                : undefined,
                                    }))}
                                />
                            </SettingControl>
                        </SettingRow>

                        {showWeekStartDay && (
                            <SettingRow isDesktop={isDesktop}>
                                <SettingInfo
                                    icon={Clock}
                                    title="Week Start Day"
                                    description="Select which day starts the work week for your organization."
                                    theme={theme}
                                />
                                <SettingControl isDesktop={isDesktop}>
                                    <MenuButton
                                        name="week_start_day"
                                        control={control}
                                        buttonStyle={styles.menuButton}
                                        wrapperProps={{ testID: 'week-start-day-menu' }}
                                        options={Object.entries(WEEK_DAY_NAMES).map(
                                            ([key, label]) => ({
                                                value: Number(key),
                                                label,
                                            })
                                        )}
                                    />
                                </SettingControl>
                            </SettingRow>
                        )}

                        <Divider />

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Settings2}
                                title="Auto-approve Geofence Entries"
                                description="Automatically approve time entries created from geofence tracking. When disabled, entries will require manual approval."
                                theme={theme}
                            />
                            <SettingControl isDesktop={isDesktop}>
                                <Switch
                                    testID="auto-approve-switch"
                                    value={formValues.auto_approve_geo_entries}
                                    onValueChange={value =>
                                        setValue('auto_approve_geo_entries', value, {
                                            shouldDirty: true,
                                        })
                                    }
                                    disabled={updateSettings.isPending}
                                />
                            </SettingControl>
                        </SettingRow>

                        <Divider />

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Building2}
                                title="Headquarters Job"
                                description="Designate one job as your HQ. Used by 'Prefer non-headquarters' travel attribution."
                                theme={theme}
                            />
                            <SettingControl isDesktop={isDesktop}>
                                <JobPicker
                                    testID="headquarters-job-picker"
                                    name="headquarters_job"
                                    control={control}
                                    orgSlug={params.orgSlug}
                                    label="Headquarters job"
                                    placeholder="No headquarters set"
                                    includeAllStatuses
                                    allowClear
                                />
                            </SettingControl>
                        </SettingRow>

                        <Divider />

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Route}
                                title="Track Travel Time"
                                description="Automatically create travel time entries when employees move between job sites."
                                theme={theme}
                            />
                            <SettingControl isDesktop={isDesktop}>
                                <MenuButton
                                    name="track_travel_time"
                                    control={control}
                                    buttonStyle={styles.menuButton}
                                    wrapperProps={{ testID: 'track-travel-time-menu' }}
                                    options={[
                                        {
                                            value: 'none',
                                            label: "Don't track",
                                        },
                                        {
                                            value: 'destination',
                                            label: 'Destination job',
                                            helperText:
                                                'Travel time is associated with the job being traveled to',
                                        },
                                        {
                                            value: 'source',
                                            label: 'Source job',
                                            helperText:
                                                'Travel time is associated with the job being traveled from',
                                        },
                                    ]}
                                />
                            </SettingControl>
                        </SettingRow>

                        <Divider />

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Route}
                                title="Prefer Non-Headquarters Job"
                                description="When travel goes between the headquarters and another job, credit the non-HQ side instead of the default. Travel between two non-HQ jobs (or two HQ trips) still uses the default."
                                theme={theme}
                            >
                                {(!formValues.headquarters_job ||
                                    formValues.track_travel_time === 'none') && (
                                    <Text
                                        variant="bodySmall"
                                        style={[
                                            styles.periodPreview,
                                            { color: theme.colors.onSurfaceVariant },
                                        ]}
                                    >
                                        {!formValues.headquarters_job
                                            ? 'Set a headquarters job above to enable this option.'
                                            : 'Enable travel tracking above to use this option.'}
                                    </Text>
                                )}
                            </SettingInfo>
                            <SettingControl isDesktop={isDesktop}>
                                <Switch
                                    testID="prefer-non-hq-switch"
                                    value={formValues.prefer_non_hq_job}
                                    onValueChange={value =>
                                        setValue('prefer_non_hq_job', value, {
                                            shouldDirty: true,
                                        })
                                    }
                                    disabled={
                                        updateSettings.isPending ||
                                        !formValues.headquarters_job ||
                                        formValues.track_travel_time === 'none'
                                    }
                                />
                            </SettingControl>
                        </SettingRow>

                        <Divider />

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Timer}
                                title="Minimum Time Entry Duration"
                                description="Geofence entries shorter than this duration (in minutes) will be discarded. This filters out accidental triggers from driving or walking past a job site."
                                theme={theme}
                            />
                            <SettingControl isDesktop={isDesktop}>
                                <NumberInput
                                    name="min_time_entry_duration"
                                    control={control}
                                    min={0}
                                    max={60}
                                    increment={1}
                                    disabled={updateSettings.isPending}
                                    wrapperProps={{ style: styles.numberInput }}
                                />
                            </SettingControl>
                        </SettingRow>

                        <Divider />

                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Timer}
                                title="Default Break Time"
                                description="Default break time duration (in minutes) for manual time entries. Users can override this when creating entries."
                                theme={theme}
                            />
                            <SettingControl isDesktop={isDesktop}>
                                <NumberInput
                                    name="default_break_time_duration"
                                    control={control}
                                    min={0}
                                    max={480}
                                    increment={5}
                                    disabled={updateSettings.isPending}
                                    wrapperProps={{ style: styles.numberInput }}
                                />
                            </SettingControl>
                        </SettingRow>

                        <Divider />

                        <WorkingHoursForm
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            enabledFieldName="working_hours_enabled"
                            startTimeFieldName="work_start_time"
                            endTimeFieldName="work_end_time"
                            workingDaysFieldName="working_days"
                            disabled={updateSettings.isPending}
                            title="Default Working Hours"
                            description="Set default working hours for all team members. Location tracking will pause outside these hours. Users can override with personal settings."
                        />
                    </View>
                </Card.Content>
            </Card>

            <Card>
                <Card.Content style={styles.cardContent}>
                    <Text variant="titleLarge" style={styles.sectionTitle}>
                        Timezone
                    </Text>
                    <View style={styles.section}>
                        <SettingRow isDesktop={isDesktop}>
                            <SettingInfo
                                icon={Globe}
                                title="Organization Timezone"
                                description="Used for working hours and clock-in reminder scheduling."
                                theme={theme}
                            />
                            <SettingControl isDesktop={isDesktop}>
                                <MenuButton
                                    name="timezone"
                                    control={control}
                                    buttonStyle={styles.timezoneButton}
                                    wrapperProps={{ testID: 'timezone-menu' }}
                                    placeholder="Select timezone"
                                    options={COMMON_TIMEZONES}
                                />
                            </SettingControl>
                        </SettingRow>
                    </View>
                </Card.Content>
            </Card>

            <ClockinRemindersCard
                isVisible={formValues.working_hours_enabled}
                isDesktop={isDesktop}
                theme={theme}
                formValues={formValues}
                setValue={setValue}
                control={control}
                disabled={updateSettings.isPending}
                supervisorOptions={supervisorOptions}
                selectedSupervisors={selectedSupervisors}
            />

            <UnsavedChangesDialog isDirty={isDirty} />
        </Page>
    )
}

function SettingRow({ isDesktop, children }: { isDesktop: boolean; children: React.ReactNode }) {
    return (
        <View
            style={[
                styles.settingRow,
                isDesktop ? styles.settingRowDesktop : styles.settingRowMobile,
            ]}
        >
            {children}
        </View>
    )
}

function SettingControl({
    isDesktop,
    children,
}: {
    isDesktop: boolean
    children: React.ReactNode
}) {
    return <View style={!isDesktop && styles.settingControlMobile}>{children}</View>
}

function ClockinRemindersCard({
    isVisible,
    isDesktop,
    theme,
    formValues,
    setValue,
    control,
    disabled,
    supervisorOptions,
    selectedSupervisors,
}: {
    isVisible: boolean
    isDesktop: boolean
    theme: MD3Theme
    formValues: OrgSettingsFormData
    setValue: ReturnType<typeof useForm<OrgSettingsFormData>>['setValue']
    control: ReturnType<typeof useForm<OrgSettingsFormData>>['control']
    disabled: boolean
    supervisorOptions: { value: string; label: string }[]
    selectedSupervisors: string[]
}) {
    if (!isVisible) return null

    const handleToggleSupervisor = (userId: string) => {
        const current = selectedSupervisors
        const updated = current.includes(userId)
            ? current.filter(id => id !== userId)
            : [...current, userId]
        setValue('default_supervisors', updated, { shouldDirty: true })
    }

    return (
        <Card>
            <Card.Content style={styles.cardContent}>
                <Text variant="titleLarge" style={styles.sectionTitle}>
                    Clock-In Reminders
                </Text>
                <View style={styles.section}>
                    <SettingRow isDesktop={isDesktop}>
                        <SettingInfo
                            icon={Bell}
                            title="Enable Clock-In Reminders"
                            description="Send push notifications to workforce users who haven't clocked in by their scheduled start time."
                            theme={theme}
                        />
                        <SettingControl isDesktop={isDesktop}>
                            <Switch
                                testID="clockin-reminders-switch"
                                value={formValues.clockin_reminders_enabled}
                                onValueChange={value =>
                                    setValue('clockin_reminders_enabled', value, {
                                        shouldDirty: true,
                                    })
                                }
                                disabled={disabled}
                            />
                        </SettingControl>
                    </SettingRow>

                    {formValues.clockin_reminders_enabled && (
                        <>
                            <Divider />

                            <SettingRow isDesktop={isDesktop}>
                                <SettingInfo
                                    icon={Timer}
                                    title="Grace Period"
                                    description="Minutes after the scheduled start time before a reminder is sent."
                                    theme={theme}
                                />
                                <SettingControl isDesktop={isDesktop}>
                                    <NumberInput
                                        name="clockin_grace_minutes"
                                        control={control}
                                        min={0}
                                        max={60}
                                        increment={5}
                                        disabled={disabled}
                                        wrapperProps={{ style: styles.numberInput }}
                                    />
                                </SettingControl>
                            </SettingRow>

                            <Divider />

                            <View style={styles.supervisorSection}>
                                <View style={styles.settingHeader}>
                                    <Users size={16} color={theme.colors.onSurfaceVariant} />
                                    <Text variant="titleMedium" style={styles.settingTitle}>
                                        Supervisors
                                    </Text>
                                </View>
                                <Text
                                    variant="bodyMedium"
                                    style={[
                                        styles.settingDescription,
                                        { color: theme.colors.onSurfaceVariant },
                                    ]}
                                >
                                    Supervisors receive alerts when their team members haven't
                                    clocked in. Select admin or clerical users.
                                </Text>
                                <View style={styles.chipContainer}>
                                    {supervisorOptions.map(option => {
                                        const isSelected = selectedSupervisors.includes(
                                            option.value
                                        )
                                        return (
                                            <Chip
                                                key={option.value}
                                                selected={isSelected}
                                                onPress={() => handleToggleSupervisor(option.value)}
                                                disabled={disabled}
                                                showSelectedOverlay
                                            >
                                                {option.label}
                                            </Chip>
                                        )
                                    })}
                                    {supervisorOptions.length === 0 && (
                                        <Text
                                            variant="bodySmall"
                                            style={{ color: theme.colors.onSurfaceVariant }}
                                        >
                                            No admin or clerical users available
                                        </Text>
                                    )}
                                </View>
                            </View>
                        </>
                    )}
                </View>
            </Card.Content>
        </Card>
    )
}

function SettingInfo({
    icon: Icon,
    title,
    description,
    theme,
    children,
}: {
    icon: LucideIcon
    title: string
    description: string
    theme: MD3Theme
    children?: React.ReactNode
}) {
    return (
        <View style={styles.settingInfo}>
            <View style={styles.settingHeader}>
                <Icon size={16} color={theme.colors.onSurfaceVariant} />
                <Text variant="titleMedium" style={styles.settingTitle}>
                    {title}
                </Text>
            </View>
            <Text
                variant="bodyMedium"
                style={[styles.settingDescription, { color: theme.colors.onSurfaceVariant }]}
            >
                {description}
            </Text>
            {children}
        </View>
    )
}

function PeriodLockingCard({
    isDesktop,
    theme,
    settings,
}: {
    isDesktop: boolean
    theme: MD3Theme
    settings: ReturnType<typeof useOrgSettings>['settings']
}) {
    const { lockPeriod, unlockPeriod, isLocking, isUnlocking } = useLockPeriod(settings)
    const lockDateStr = (settings as unknown as { entries_locked_until?: string } | null)
        ?.entries_locked_until
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [pickerDate, setPickerDate] = useState<Date | null>(null)

    const formattedLockDate = lockDateStr
        ? new Date(lockDateStr).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : null

    const handleOpenPicker = () => {
        setPickerDate(lockDateStr ? new Date(lockDateStr) : new Date())
        setShowDatePicker(true)
    }

    const handleSaveDate = () => {
        if (!pickerDate) return
        const iso = pickerDate.toISOString()
        lockPeriod({ lockDate: iso })
        setShowDatePicker(false)
    }

    const handleClearLock = () => {
        unlockPeriod()
        setShowDatePicker(false)
    }

    return (
        <Card>
            <Card.Content style={styles.cardContent}>
                <Text variant="titleLarge" style={styles.sectionTitle}>
                    Period Locking
                </Text>
                <View style={styles.section}>
                    <SettingRow isDesktop={isDesktop}>
                        <SettingInfo
                            icon={Lock}
                            title="Entries Locked Until"
                            description="Workforce users cannot create or edit time entries before this date. Set from the approval screen by locking a period."
                            theme={theme}
                        >
                            {formattedLockDate ? (
                                <Text
                                    variant="bodyMedium"
                                    style={{ color: theme.colors.primary, fontWeight: '600' }}
                                >
                                    {formattedLockDate}
                                </Text>
                            ) : (
                                <Text
                                    variant="bodyMedium"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    No lock set
                                </Text>
                            )}
                        </SettingInfo>
                        <SettingControl isDesktop={isDesktop}>
                            {formattedLockDate ? (
                                <Button
                                    title="Adjust Lock Date"
                                    onPress={handleOpenPicker}
                                    icon="calendar-edit"
                                />
                            ) : null}
                        </SettingControl>
                    </SettingRow>
                </View>
            </Card.Content>

            <Portal>
                <Dialog
                    visible={showDatePicker}
                    onDismiss={() => setShowDatePicker(false)}
                    style={styles.lockDialog}
                >
                    <Dialog.Title>Adjust Lock Date</Dialog.Title>
                    <Dialog.Content>
                        <Text
                            variant="bodyMedium"
                            style={{
                                color: theme.colors.onSurfaceVariant,
                                marginBottom: 16,
                            }}
                        >
                            Entries before this date will be locked for workforce users.
                        </Text>
                        <DateTimePicker
                            value={pickerDate}
                            onChange={setPickerDate}
                            mode="date"
                            label="Lock entries until"
                        />
                    </Dialog.Content>
                    <Dialog.Actions style={styles.lockDialogActions}>
                        <Button
                            title="Clear Lock"
                            onPress={handleClearLock}
                            loading={isUnlocking}
                            disabled={isUnlocking || isLocking}
                            color={theme.colors.error}
                        />
                        <View style={{ flex: 1 }} />
                        <Button
                            title="Cancel"
                            onPress={() => setShowDatePicker(false)}
                            disabled={isLocking}
                        />
                        <Button
                            title="Save"
                            onPress={handleSaveDate}
                            loading={isLocking}
                            disabled={isLocking || !pickerDate}
                        />
                    </Dialog.Actions>
                </Dialog>
            </Portal>
        </Card>
    )
}

const styles = StyleSheet.create({
    cardContent: {
        gap: 16,
        paddingBottom: 16,
    },
    sectionTitle: {
        fontWeight: '600',
    },
    section: {
        gap: 12,
    },
    settingRow: {
        gap: 12,
    },
    settingRowDesktop: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    settingRowMobile: {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    settingControlMobile: {
        alignSelf: 'flex-end',
    },
    settingInfo: {
        flex: 1,
        gap: 8,
    },
    settingHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    settingTitle: {
        fontWeight: '600',
    },
    settingDescription: {
        lineHeight: 20,
    },
    periodPreview: {
        fontStyle: 'italic',
        marginTop: 4,
    },
    menuButton: {
        minWidth: 150,
    },
    numberInput: {
        marginBottom: 0,
        minWidth: 180,
    },
    timezoneButton: {
        minWidth: 200,
    },
    supervisorSection: {
        gap: 8,
    },
    chipContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    lockDialog: {
        maxWidth: 400,
        alignSelf: 'center',
    },
    lockDialogActions: {
        flexDirection: 'row',
        gap: 8,
    },
})
